<template>
  <div class="skincare-finder__question">
    <div class="skincare-finder__question-container container container--main">
      <div class="skincare-finder__question-title">
        {{ question.title }}
      </div>
      <div class="skincare-finder__question-copy">
        {{ question.copy }}
      </div>
      <hr>

      <div
        v-if="question.isMultipleSelection && question.helpText"
        class="skincare-finder__question-helper-text"
      >
        {{ question.helpText }}
      </div>

      <form
        ref="form"
        class="skincare-finder__question__form"
        @submit.prevent
      >
        <div
          class="skincare-finder__question__answers"
          :style="gridTemplateColumns"
        >
          <skincare-finder-answer
            v-for="(answer, i) in question.answers"
            :key="i"
            :answer="answer"
            :is-multiple-selection="question.isMultipleSelection"
            :tooltip-close-alt="tooltipCloseAlt"
            :tooltip-open-alt="tooltipOpenAlt"
          />
        </div>
        <button
          class="btn btn--primary"
          :value="question.nextCTALabel"
          @click.prevent="nextQuestion()"
        >
          {{ question.nextCTALabel }}
          <svg class="icon">
            <use xlink:href="#full-arrow" />
          </svg>
        </button>
        <div
          v-show="displayError"
          class="skincare-finder__question-error-message"
        >
          {{ question.nextCTAErrorMessage }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import { clickoutside } from '@Foundation/directives/clickoutside/clickoutside';
  import SkincareFinderAnswer from '@Feature/SkincareFinder/code/Scripts/components/SkincareFinderAnswer/SkincareFinderAnswer.vue';
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';
  import { mapGetters } from 'vuex';


  export default {
    name: 'SkincareFinderQuestion',
    directives: {
      clickoutside
    },
    components: {
      SkincareFinderAnswer
    },
    props: {
      question: { type: Object, required: true },
      tooltipCloseAlt: { type: String, required: false, default: 'Close Info' },
      tooltipOpenAlt: { type: String, required: false, default: 'Open Info' },
      taggingCategory: { type: String, required: false, default: 'skin care quiz' }
    },
    data() {
      return {
        selectedAnswers: [],
        displayingTooltip: '',
        displayError: false,
        smallScreen: !window.matchMedia('(min-width: 1025px)').matches
      };
    },
    computed: {
      ...mapGetters('quizStore', [
        'answeredQuestionsCount'
      ]),
      gridTemplateColumns() {
        if (!this.question.answers) {
          return '';
        }
        return this.smallScreen ? 'grid-template-columns: repeat(1, 1fr)' : `grid-template-columns: repeat(${this.question.answers.length > 4 ? Math.round(parseFloat(this.question.answers.length / 2)) : this.question.answers.length }, minmax(0, 335px));`;
      }
    },
    mounted() {
      this.$on('checked-answer', this.check);
    },
    methods: {
      taggingLabel() {
        let answers = '';
        this.selectedAnswers.forEach(id => {
          const label = this.question.answers.find(answer => answer.id === id).label;
          answers = answers + (answers ? `::${label}` : label);
        });
        return answers;
      },
      clearSelections() {
        this.selectedAnswers = [];
        this.$refs.form.reset();
        this.displayError = false;
      },
      getNextQuestionId() {
        return this.question.answers.find(element => element.id === this.selectedAnswers[0]).nextQuestionId || this.question.defaultNextScreen;
      },
      check(checked) {
        const event = checked.event;
        const id = checked.id;
        if (this.question.isMultipleSelection && event.target.checked && this.selectedAnswers.length >= this.question.maxAnswersAllowed) {
          event.target.checked = false;
          return;
        }

        if (event.target.checked) {
          this.addAnswer(id);
        } else {
          this.removeAnswer(id);
        }
      },
      removeAnswer(id) {
        if (this.selectedAnswers.indexOf(id) >= 0) {
          this.selectedAnswers.splice(this.selectedAnswers.indexOf(id), 1);
        }
      },
      addAnswer(id) {
        if (this.selectedAnswers.indexOf(id) >= 0) {
          return;
        }
        if (!this.question.isMultipleSelection) {
          this.selectedAnswers = [];
        }
        this.selectedAnswers.push(id);
      },
      nextQuestion() {
        if (!this.selectedAnswers.length) {
          this.displayError = true;
          return;
        }

        const tagging = {
          'event': 'uaevent',
          'ecommerce': 'undefined',
          'eventCategory': this.taggingCategory,
          'eventAction': `${this.answeredQuestionsCount}::${this.question.title}`,
          'eventLabel': this.taggingLabel()
        };

        AnalyticsHandler.pushDataLayer(tagging);

        this.$parent.$emit(
          'next-question',
          {
            id: this.question.id,
            answers: this.selectedAnswers,
            nextQuestionId: this.getNextQuestionId(),
            isLastQuestion: this.question.isLastQuestion
          }
        );
        this.clearSelections();
      },
      isSelected(id) {
        return this.selectedAnswers.indexOf(id) >= 0;
      }
    }
  };
</script>
